/**
 * Edição da vacina do catálogo (#530). Não há leitura por id na API: a vacina sai da lista.
 * Com dose registrada, o servidor recusa mudar os meses do esquema — a recusa aparece no
 * formulário, junto do botão de salvar.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Card, CardContent, CircularProgress } from '@mui/material';
import { PageShell } from '../../components';
import { vaccineTypeService } from '../../services/vaccineTypeService';
import { getApiErrorMessage } from '../../types/common';
import type { VaccineType, VaccineTypePayload } from '../../types/vaccination';
import { VaccineTypeForm } from './components/VaccineTypeForm';

const BREADCRUMBS = [
  { label: 'Início', path: '/' },
  { label: 'Vacinas', path: '/vaccine-types' },
  { label: 'Editar vacina' },
];

export const VaccineTypeEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [vacina, setVacina] = useState<VaccineType | null>(null);
  const [loading, setLoading] = useState(true);
  const [erroAoCarregar, setErroAoCarregar] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true);
        const vacinas = await vaccineTypeService.getAll();
        setVacina(vacinas.find((item) => String(item.id) === String(id)) ?? null);
      } catch (err: unknown) {
        setErroAoCarregar(getApiErrorMessage(err, 'Erro ao carregar a vacina'));
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [id]);

  const handleSubmit = async (data: VaccineTypePayload) => {
    if (!vacina) return;
    try {
      setIsSubmitting(true);
      setErro(null);
      await vaccineTypeService.update(vacina.id, data);
      navigate('/vaccine-types');
    } catch (err: unknown) {
      setErro(getApiErrorMessage(err, 'Erro ao salvar a vacina'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar vacina">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!vacina) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar vacina">
        <Alert severity="error">{erroAoCarregar ?? 'Vacina não encontrada no catálogo.'}</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={BREADCRUMBS} title="Editar vacina" subtitle={vacina.name}>
      <Card>
        <CardContent>
          <VaccineTypeForm
            vacina={vacina}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/vaccine-types')}
            isSubmitting={isSubmitting}
            erro={erro}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default VaccineTypeEditPage;
